import { RadioGroup, TextButton as DSTextButton } from '@wix/design-system'
import { useContent, usePageLoading } from '@wix/funnel-builder-lizard'
import { Button as WildcatButton, TextButton as WildcatTextButton } from '@wix/wildcat-ui'
import { ArrowRight } from '@wix/wix-ui-icons-common'
import classNames from 'classnames'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePageLogger } from '../../../bi/mfi'
import { useNext } from '../../../hooks/useNext'
import { usePrevious } from '../../../utils/usePrevious'
import { Button as HarmonyButton } from '../../reusable/Button'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import ArrowRightHarmony from '../../reusable/icons/ArrowRightHarmony.svg'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { RadioOption } from '../../reusable/RadioOption'
import { RichText } from '../../reusable/RichText'
import { TWelcomeV2 as Content } from './WelcomeV2.content'
import { welcomeV2DataHooks as dataHooks } from './WelcomeV2.dataHooks'
import s from './WelcomeV2.scss'

type ElementType<T> = T extends (infer U)[] ? U : never
type Option = ElementType<Content['question']['options']>

export const WelcomeV2: FC = () => {
  const { content } = useContent<Content>()

  if (content.skin === 'harmony') {
    return (
      <DesignSystemTokensLoader type="odeditor">
        <WelcomeV2Template />
      </DesignSystemTokensLoader>
    )
  }

  return <WelcomeV2Template />
}

const WelcomeV2Template: FC = () => {
  const pageLogger = usePageLogger()
  const next = useNext()
  const { content } = useContent<Content>()
  const { skip, next: nextBtn, question, legalDisclaimer } = content
  const { questionKey, content: questionContent, options: questionOptions } = question
  const [searchParams] = useSearchParams()
  const flowContext = searchParams.get('flowContext')
  const prevFlowContext = usePrevious(flowContext)
  const [pageLoading] = usePageLoading()
  const isHarmony = content.skin === 'harmony'
  const precheckedOption = useMemo(() => {
    return content.question.options.find(option => option.isChecked)
  }, [content])

  const [clickedButton, setClickedButton] = useState<'next' | 'skip'>()

  const [selectedOption, setSelectedOption] = useState<Option | undefined>(precheckedOption)

  const handleChange = (value?: number | string) => {
    const option = questionOptions.find(option => option.optionKey === value)
    if (!option) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedOption(option)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleNext = async () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('next')
    void pageLogger.answer({ answer: selectedOption?.optionKey || '' })
    next({
      [questionKey]: { values: [selectedOption?.optionKey ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (prevFlowContext !== flowContext && !pageLoading) {
      setClickedButton(undefined)
      setSelectedOption(precheckedOption)
    }
  }, [clickedButton, flowContext, prevFlowContext, pageLoading, precheckedOption])

  return (
    <div className={classNames(s.container, { [s.containerHarmony]: isHarmony })}>
      <WixLogo className={s.logo} />
      <div className={s.cardGroup}>
        <div className={s.leftCard}>
          <h2 className={s.subtitle} data-hook={dataHooks.subtitle()}>
            {questionContent.subtitle}
          </h2>
          <h1 className={s.title} data-hook={dataHooks.title()}>
            {questionContent.title}
          </h1>
          {isHarmony ? (
            <RadioGroup
              value={selectedOption?.optionKey}
              onChange={handleChange}
              selectionArea="always"
              selectionAreaSkin="outlined"
              selectionAreaPadding="20px 24px"
              spacing="12px"
              className={s.radioGroupHarmony}
            >
              {questionOptions.map(option => (
                <RadioGroup.Radio
                  key={option.optionKey}
                  value={option.optionKey}
                  dataHook={dataHooks.radio({ optionKey: option.optionKey })}
                >
                  <span className={s.radioLabelHarmony}>{option.title}</span>
                </RadioGroup.Radio>
              ))}
            </RadioGroup>
          ) : (
            <div className={s.radioGroup}>
              {questionOptions.map(option => (
                <RadioOption
                  key={option.optionKey}
                  name={questionKey}
                  value={option.optionKey}
                  checked={selectedOption?.optionKey === option.optionKey}
                  onChange={() => handleChange(option.optionKey)}
                  label={option.title}
                  dataHook={dataHooks.radio({ optionKey: option.optionKey })}
                />
              ))}
            </div>
          )}
          {isHarmony ? (
            <HarmonyButton
              isLoading={clickedButton === 'next'}
              onClick={handleNext}
              disabled={selectedOption === undefined}
              className={s.button}
              size="large"
              skin="standard"
              dataHook={dataHooks.next()}
              label={nextBtn}
            />
          ) : (
            <WildcatButton
              isLoading={clickedButton === 'next'}
              onClick={handleNext}
              disabled={selectedOption === undefined}
              className={s.button}
              size="large"
              dataHook={dataHooks.next()}
            >
              {nextBtn}
            </WildcatButton>
          )}
          {legalDisclaimer && (
            <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {legalDisclaimer}
            </RichText>
          )}
        </div>
        <div className={s.videoContainer}>
          <video className={s.video} src={questionContent.src} loop autoPlay muted />
        </div>
      </div>
      {isHarmony ? (
        <DSTextButton
          onClick={handleSkip}
          className={s.skipButtonHarmony}
          suffixIcon={<ArrowRightHarmony />}
          dataHook={dataHooks.skip()}
          skin="dark"
          underline="none"
          size="medium"
        >
          {skip}
        </DSTextButton>
      ) : (
        <WildcatTextButton
          onClick={handleSkip}
          className={s.skipButton}
          suffixIcon={<ArrowRight />}
          dataHook={dataHooks.skip()}
        >
          {skip}
        </WildcatTextButton>
      )}
    </div>
  )
}
